import { useState } from 'react';
import { Tooltip } from '../display/Tooltip';
import { getInitials } from '@airclou/utils';

interface WorkspaceItem {
  id: string;
  name: string;
  slug?: string;
  role?: 'admin' | 'member';
}

interface WorkspacesStackProps {
  workspaces: WorkspaceItem[];
  maxVisible?: number;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
  showTooltip?: boolean;
}

export function WorkspacesStack({ 
  workspaces, 
  maxVisible = 3, 
  size = 'md',
  className = '',
  showTooltip = true
}: WorkspacesStackProps) {
  const visibleWorkspaces = workspaces.slice(0, maxVisible);
  const remainingCount = Math.max(0, workspaces.length - maxVisible);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-8 h-8 text-sm',
    lg: 'w-10 h-10 text-base'
  };

  const getTooltipContent = (workspace: WorkspaceItem) => {
    const parts = [workspace.name];
    if (workspace.role) {
      parts.push(`Role: ${workspace.role}`);
    }
    return parts.join(' • ');
  };

  const getWorkspaceClasses = () => {
    const baseClasses = `relative ${sizeClasses[size]} rounded-lg border border-white dark:border-gray-800 flex items-center justify-center font-medium shadow-sm cursor-default`;
    return `${baseClasses} bg-emerald-600 text-white`;
  };

  // Format large numbers for better display in small rectangles
  const formatOverflowCount = (count: number) => {
    if (count >= 1000) {
      return `+${Math.floor(count / 1000)}k`;
    }
    if (count >= 100) {
      return `+${count}`;
    }
    return `+${count}`;
  };

  // Get appropriate font size for overflow count
  const getOverflowFontSize = (count: number) => {
    const baseSizeClasses = {
      xs: count >= 100 ? 'text-[7px]' : 'text-[8px]',
      sm: count >= 100 ? 'text-[7px]' : 'text-[9px]',
      md: count >= 100 ? 'text-[8px]' : 'text-[10px]',
      lg: count >= 100 ? 'text-[10px]' : 'text-xs'
    };
    return baseSizeClasses[size];
  };

  const getOverflowClassesWithCount = (count: number) => {
    const baseClasses = `relative ${sizeClasses[size]} rounded-lg border border-white dark:border-gray-800 flex items-center justify-center font-medium shadow-sm cursor-default ${getOverflowFontSize(count)}`;
    return `${baseClasses} bg-gray-400 dark:bg-gray-500 text-white`;
  };

  if (workspaces.length === 0) {
    return (
      <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
    );
  }

  return (
    <div className={`flex items-center -space-x-2 ${className}`}>
      {visibleWorkspaces.map((workspace, index) => (
        <Tooltip
          key={workspace.id}
          content={showTooltip ? getTooltipContent(workspace) : ''}
          show={showTooltip && hoveredIndex === index}
          position="top"
          delay={300}
          disabled={!showTooltip}
        >
          <div
            className={getWorkspaceClasses()}
            style={{ zIndex: maxVisible - index }}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <span>{getInitials(workspace.name)}</span>
          </div>
        </Tooltip>
      ))}
      {remainingCount > 0 && (
        <Tooltip
          content={showTooltip ? `${remainingCount} more workspace${remainingCount > 1 ? 's' : ''}` : ''}
          show={showTooltip && hoveredIndex === visibleWorkspaces.length}
          position="top"
          delay={300}
          disabled={!showTooltip}
        >
          <div
            className={getOverflowClassesWithCount(remainingCount)}
            style={{ zIndex: 0 }}
            onMouseEnter={() => setHoveredIndex(visibleWorkspaces.length)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            {formatOverflowCount(remainingCount)}
          </div>
        </Tooltip>
      )}
    </div>
  );
}
